import React, { useState, useRef, useEffect } from 'react';
import { Upload, Button, message, List, Card } from 'antd';
import { UploadOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { uploadDashboardMedia, deleteDashboardMedia } from '../api/DashboardMedia';

/**
 * 媒體上傳組件
 * @param {number} maxCount - 最大上傳數量
 * @param {string} accept - 接受的檔案類型
 * @param {string} mediaType - 媒體類型 ('image' 或 'video')
 * @param {Array} value - 當前已上傳的檔案 URL 陣列
 * @param {Function} onChange - 值變更回調
 */
const MediaUploader = ({ maxCount = 1, accept, mediaType = 'image', value = [], onChange }) => {
  const [uploading, setUploading] = useState(false);
  // 用 ref 追蹤最新 value，避免多檔並發上傳時 stale closure 互相覆蓋
  // value 可能傳入 null（初始化），統一轉為 []
  const toArr = (v) => (Array.isArray(v) ? v : []);
  const accRef = useRef(toArr(value));
  useEffect(() => { accRef.current = toArr(value); }, [value]);

  const handleUpload = async ({ file, onSuccess, onError }) => {
    if ((accRef.current?.length || 0) >= maxCount) {
      message.warning(`最多只能上傳 ${maxCount} 個檔案`);
      onError(new Error('已達到最大上傳數量'));
      return;
    }

    setUploading(true);
    try {
      const result = await uploadDashboardMedia([file], mediaType);
      const uploadedUrls = result.files.map(f => f.url);
      const newValue = [...accRef.current, ...uploadedUrls];
      accRef.current = newValue;
      onChange(newValue);
      onSuccess('ok');
      message.success(`檔案上傳成功`);
    } catch (error) {
      onError(error);
      message.error('上傳失敗: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (urlToDelete) => {
    try {
      // 從 URL 提取檔案名稱
      const filename = urlToDelete.split('/').pop();
      await deleteDashboardMedia(filename);
      
      // 從列表中移除
      const newValue = value.filter(url => url !== urlToDelete);
      onChange(newValue);
      message.success('檔案已刪除');
    } catch (error) {
      message.error('刪除失敗: ' + (error.response?.data?.error || error.message));
    }
  };

  const handlePreview = (url) => {
    window.open(url, '_blank');
  };

  return (
    <div>
      <Upload
        customRequest={handleUpload}
        accept={accept}
        multiple={maxCount > 1}
        showUploadList={false}
        beforeUpload={(file) => {
          // 檢查檔案數量
          if ((value?.length || 0) >= maxCount) {
            message.warning(`最多只能上傳 ${maxCount} 個檔案`);
            return Upload.LIST_IGNORE;
          }
          return true;
        }}
      >
        <Button 
          icon={<UploadOutlined />} 
          loading={uploading}
          disabled={(value?.length || 0) >= maxCount}
        >
          {uploading ? '上傳中...' : `選擇檔案 (${value?.length || 0}/${maxCount})`}
        </Button>
      </Upload>

      {/* 已上傳檔案列表 */}
      {value && value.length > 0 && (
        <List
          style={{ marginTop: 16 }}
          grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 4, xxl: 4 }}
          dataSource={value}
          renderItem={(url, index) => (
            <List.Item>
              <Card
                size="small"
                cover={
                  mediaType === 'image' ? (
                    <img
                      alt={`上傳檔案 ${index + 1}`}
                      src={url}
                      style={{ width: '100%', height: 120, objectFit: 'cover' }}
                    />
                  ) : mediaType === 'slideshow' ? (
                    <div style={{ width: '100%', height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', fontSize: 32 }}>
                      📄
                    </div>
                  ) : (
                    <video
                      src={url}
                      style={{ width: '100%', height: 120, objectFit: 'cover' }}
                      controls={false}
                    />
                  )
                }
                actions={[
                  <EyeOutlined key="preview" onClick={() => handlePreview(url)} />,
                  <DeleteOutlined key="delete" onClick={() => handleDelete(url)} style={{ color: '#ff4d4f' }} />,
                ]}
              >
                <Card.Meta description={`檔案 ${index + 1}`} />
              </Card>
            </List.Item>
          )}
        />
      )}
    </div>
  );
};

export default MediaUploader;
